import { useState, useRef, useEffect, useCallback } from 'react'
import { Bell, CheckCircle2, Clock, AlertTriangle, Zap, Shield, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  type AppNotification,
} from '../../services/notifications.api'

interface NotificationsDropdownProps {
  scrolled: boolean
}

function getNotifIcon(type: string) {
  switch (type.toLowerCase()) {
    case 'maintenance': return <Zap className="w-4 h-4 text-yellow-600" />
    case 'booking': return <Clock className="w-4 h-4 text-[hsl(var(--brand-primary))]" />
    case 'warranty': return <Shield className="w-4 h-4 text-purple-600" />
    case 'issue': case 'warning': return <AlertTriangle className="w-4 h-4 text-red-600" />
    case 'success': return <CheckCircle2 className="w-4 h-4 text-green-600" />
    default: return <Bell className="w-4 h-4 text-muted-foreground" />
  }
}

function getNotifBg(type: string) {
  switch (type.toLowerCase()) {
    case 'maintenance': return 'bg-yellow-50 border-yellow-200'
    case 'booking': return 'bg-[hsl(var(--brand-primary)/0.07)] border-[hsl(var(--brand-primary)/0.2)]'
    case 'warranty': return 'bg-purple-50 border-purple-200'
    case 'issue': case 'warning': return 'bg-red-50 border-red-200'
    case 'success': return 'bg-green-50 border-green-200'
    default: return 'bg-secondary border-border'
  }
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function NotificationsDropdown({ scrolled: _scrolled }: NotificationsDropdownProps) {
  const { t } = useTranslation('layout')
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(false)
  const notificationRef = useRef<HTMLDivElement>(null)

  const unreadCount = notifications.filter(n => !n.isRead).length

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getNotifications()
      setNotifications(data)
    } catch {
      // silently ignore — no network or not logged in
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false)
      }
    }
    if (showNotifications) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showNotifications])

  const handleMarkRead = async (id: string) => {
    await markNotificationRead(id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n))
  }

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead()
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
  }

  const handleDismiss = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    // Optimistically remove from UI immediately, then delete on server
    setNotifications(prev => prev.filter(n => n.id !== id))
    try {
      await deleteNotification(id)
    } catch {
      // If server delete fails, silently ignore — UI is already clean
    }
  }

  return (
    <div className="relative" ref={notificationRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-9 w-9 border border-border hover:bg-secondary hover:text-foreground transition-colors"
        onClick={() => setShowNotifications(!showNotifications)}
        aria-label={`${t('notifications.title')}${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-4 h-4 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] rounded-full font-semibold z-10 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {showNotifications && (
        <div className="absolute right-0 top-11 w-96 max-w-[calc(100vw-1rem)] bg-card border border-border rounded-xl shadow-xl z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground text-sm">{t('notifications.title')}</h3>
              {unreadCount > 0 && (
                <span className="text-[10px] font-semibold bg-red-500 text-white rounded-full px-1.5 py-0.5">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
              <button
                onClick={() => setShowNotifications(false)}
                className="p-1 rounded hover:bg-secondary transition-colors"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground animate-pulse">
                {t('notifications.loading')}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 flex flex-col items-center gap-2 text-center">
                <Bell className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">{t('notifications.empty')}</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => !n.isRead && handleMarkRead(n.id)}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-secondary/40 transition-colors group',
                    !n.isRead && 'bg-secondary/20'
                  )}
                >
                  <div className={cn('h-8 w-8 rounded-lg border flex items-center justify-center flex-shrink-0 mt-0.5', getNotifBg(n.type))}>
                    {getNotifIcon(n.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground line-clamp-2 leading-relaxed">
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{n.body}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.createdAt)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {!n.isRead && (
                      <div className="h-2 w-2 rounded-full bg-[hsl(var(--brand-primary))] mt-1.5" />
                    )}
                    <button
                      onClick={(e) => handleDismiss(e, n.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted"
                      title={t('notifications.dismiss')}
                    >
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
